import { useRef, useEffect, useState, useCallback } from "react";
import type {
  IntrospectionType,
  IntrospectionField,
  IntrospectionArg,
  IntrospectionEnumValue,
} from "../../types/introspection";
import {
  formatTypeRef,
  kindColor,
  kindBadgeColor,
} from "./schemaUtils";
import { SimpleMarkdown } from "./SimpleMarkdown";

// ── Column descriptor types ─────────────────────────────────────────

export type ColumnDescriptor =
  | { kind: "type-list"; types: IntrospectionType[]; rootTypeNames: Set<string>; selectedName?: string }
  | { kind: "type-detail"; type: IntrospectionType; isRoot: boolean; selectedFieldName?: string }
  | { kind: "field-detail"; field: IntrospectionField; parentTypeName: string; selectedArgType?: string; selectedReturnType?: boolean };

interface SchemaColumnProps {
  column: ColumnDescriptor;
  onSelectType: (name: string) => void;
  onSelectField: (typeName: string, fieldName: string) => void;
  onSelectArg?: (parentTypeName: string, fieldName: string, argTypeName: string) => void;
  onSelectReturnType?: (parentTypeName: string, fieldName: string) => void;
  isLast: boolean;
}

export function SchemaColumn({ column, onSelectType, onSelectField, onSelectArg, onSelectReturnType, isLast }: SchemaColumnProps) {
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isLast && scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [column, isLast]);

  if (column.kind === "type-list") {
    return (
      <TypeListColumn
        types={column.types}
        rootTypeNames={column.rootTypeNames}
        {...(column.selectedName != null ? { selectedName: column.selectedName } : {})}
        onSelect={onSelectType}
        scrollRef={scrollRef}
      />
    );
  }

  if (column.kind === "field-detail") {
    return (
      <FieldDetailColumn
        field={column.field}
        parentTypeName={column.parentTypeName}
        {...(column.selectedArgType != null ? { selectedArgType: column.selectedArgType } : {})}
        {...(column.selectedReturnType != null ? { selectedReturnType: column.selectedReturnType } : {})}
        {...(onSelectType !== undefined ? { onSelectType } : {})}
        {...(onSelectArg !== undefined ? { onSelectArg } : {})}
        {...(onSelectReturnType !== undefined ? { onSelectReturnType } : {})}
        scrollRef={scrollRef}
      />
    );
  }

  return (
    <TypeDetailColumn
      type={column.type}
      isRoot={column.isRoot}
      {...(column.selectedFieldName != null ? { selectedFieldName: column.selectedFieldName } : {})}
      onSelectField={onSelectField}
      onSelectType={onSelectType}
      scrollRef={scrollRef}
    />
  );
}

// ── Helpers ─────────────────────────────────────────────────────────

/** Truncate with ellipsis in the middle: "ArticleCustom…nfiguration" */
function middleEllipsis(text: string, maxLen: number): string {
  if (text.length <= maxLen) return text;
  const half = Math.floor((maxLen - 1) / 2);
  const endLen = maxLen - half - 1;
  return text.slice(0, half) + "…" + text.slice(text.length - endLen);
}

// Column width constant
const COL_WIDTH = "w-80";
const ROOT_GROUP_HEADER_CLASS = "sticky top-0 z-10 bg-schema-root-soft px-3 py-1.5 text-[10px] font-medium uppercase tracking-wider text-schema-root";
const ROOT_ITEM_SELECTED_CLASS = "bg-schema-root-strong text-schema-root";
const ROOT_ITEM_IDLE_CLASS = "bg-schema-root-soft text-schema-root hover:bg-schema-root-strong";
const ABSTRACT_CHIP_CLASS = "rounded bg-schema-abstract-bg px-1.5 py-0.5 text-[10px] font-medium text-schema-abstract hover:bg-surface-overlay";
const DEPRECATED_PANEL_CLASS = "mt-1.5 rounded bg-schema-deprecated-bg px-2 py-1";
const DEPRECATED_BADGE_CLASS = "shrink-0 rounded bg-schema-deprecated-bg px-1 py-0.5 text-[8px] font-bold uppercase text-schema-deprecated";
const DEPRECATED_LABEL_CLASS = "text-[9px] font-bold uppercase text-schema-deprecated";
const DEPRECATED_TEXT_CLASS = "max-w-[280px] text-[10px] leading-relaxed text-schema-deprecated";
const TYPE_LINK_CLASS = "text-schema-link";
const TYPE_LINK_HOVER_CLASS = "text-schema-link hover:text-schema-link-hover";

// ── Instant tooltip ─────────────────────────────────────────────────

function Tooltip({ text, children }: { text: string; children: React.ReactNode }) {
  const [show, setShow] = useState(false);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const ref = useRef<HTMLSpanElement>(null);

  const handleEnter = useCallback((e: React.MouseEvent) => {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setPos({ x: rect.left, y: rect.bottom + 4 });
    setShow(true);
  }, []);

  const handleLeave = useCallback(() => setShow(false), []);

  // Don't show tooltip if text matches display (not truncated)
  const childText = ref.current?.textContent ?? "";
  const isTruncated = childText !== text;

  return (
    <span ref={ref} onMouseEnter={handleEnter} onMouseLeave={handleLeave} className="truncate">
      {children}
      {show && isTruncated && (
        <span
          className="fixed z-50 max-w-xs rounded bg-zinc-900 border border-zinc-700 px-2 py-1 text-[11px] text-zinc-200 shadow-lg pointer-events-none"
          style={{ left: pos.x, top: pos.y }}
        >
          {text}
        </span>
      )}
    </span>
  );
}

// ── Type list column ────────────────────────────────────────────────

function TypeListColumn({
  types,
  rootTypeNames,
  selectedName,
  onSelect,
  scrollRef,
}: {
  types: IntrospectionType[];
  rootTypeNames: Set<string>;
  selectedName?: string;
  onSelect: (name: string) => void;
  scrollRef: React.RefObject<HTMLDivElement | null>;
}) {
  const roots = types.filter((t) => rootTypeNames.has(t.name));
  const objects = types.filter((t) => !rootTypeNames.has(t.name) && t.kind === "OBJECT");
  const inputObjects = types.filter((t) => t.kind === "INPUT_OBJECT");
  const enums = types.filter((t) => t.kind === "ENUM");
  const interfaces = types.filter((t) => t.kind === "INTERFACE");
  const unions = types.filter((t) => t.kind === "UNION");
  const scalars = types.filter((t) => t.kind === "SCALAR");

  const groups: { label: string; items: IntrospectionType[]; isRoot?: boolean }[] = [
    { label: "Root Types", items: roots, isRoot: true },
    { label: "Objects", items: objects },
    { label: "Input Objects", items: inputObjects },
    { label: "Enums", items: enums },
    { label: "Interfaces", items: interfaces },
    { label: "Unions", items: unions },
    { label: "Scalars", items: scalars },
  ].filter((g) => g.items.length > 0);

  return (
    <div ref={scrollRef} className={`flex h-full ${COL_WIDTH} shrink-0 flex-col overflow-y-auto border-r border-border`}>
      {groups.map((group) => (
        <div key={group.label}>
          {group.isRoot ? (
            <div className={ROOT_GROUP_HEADER_CLASS}>
              {group.label}
            </div>
          ) : (
            <div className="sticky top-0 z-10 bg-surface-raised/95 backdrop-blur-sm px-3 py-1.5 text-[10px] font-medium uppercase tracking-wider text-zinc-600">
              {group.label}
            </div>
          )}
          {group.items.map((type) => {
            const isSelected = selectedName === type.name;
            const isRoot = rootTypeNames.has(type.name);
            const display = middleEllipsis(type.name, 28);
            return (
              <button
                key={type.name}
                onClick={() => onSelect(type.name)}
                className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs transition-colors ${
                  isRoot
                    ? isSelected
                      ? ROOT_ITEM_SELECTED_CLASS
                      : ROOT_ITEM_IDLE_CLASS
                    : isSelected
                      ? "bg-surface-overlay text-zinc-100"
                      : "text-zinc-400 hover:bg-surface-raised hover:text-zinc-300"
                }`}
              >
                <Tooltip text={type.name}>
                  <span className={`font-medium ${isRoot ? "" : kindColor(type.kind, false)}`}>
                    {display}
                  </span>
                </Tooltip>
                <span className={`ml-auto shrink-0 rounded px-1.5 py-0.5 text-[9px] font-medium ${kindBadgeColor(type.kind)}`}>
                  {type.kind}
                </span>
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}

// ── Type detail column ──────────────────────────────────────────────

function TypeDetailColumn({
  type,
  isRoot,
  selectedFieldName,
  onSelectField,
  onSelectType,
  scrollRef,
}: {
  type: IntrospectionType;
  isRoot: boolean;
  selectedFieldName?: string;
  onSelectField: (typeName: string, fieldName: string) => void;
  onSelectType: (name: string) => void;
  scrollRef: React.RefObject<HTMLDivElement | null>;
}) {
  const fields = type.fields ?? type.inputFields ?? [];
  const enumValues = type.enumValues ?? [];
  const interfaces = type.interfaces ?? [];
  const possibleTypes = type.possibleTypes ?? [];

  const headerDisplay = middleEllipsis(type.name, 30);

  return (
    <div ref={scrollRef} className={`flex h-full ${COL_WIDTH} shrink-0 flex-col overflow-y-auto border-r border-border`}>
      {/* Type header */}
      <div className="sticky top-0 z-10 border-b border-border bg-surface-raised/95 backdrop-blur-sm px-3 py-2">
        <div className="flex items-center gap-2">
          <Tooltip text={type.name}>
            <span className={`text-sm font-semibold ${kindColor(type.kind, isRoot)}`}>
              {headerDisplay}
            </span>
          </Tooltip>
          <span className={`shrink-0 rounded px-1.5 py-0.5 text-[9px] font-medium ${kindBadgeColor(type.kind)}`}>
            {type.kind}
          </span>
        </div>
        {type.description && (
          <CollapsibleDescription text={type.description} />
        )}
      </div>

      {/* Interfaces implemented */}
      {interfaces.length > 0 && (
        <div className="border-b border-border px-3 py-2">
          <div className="mb-1 text-[10px] font-medium uppercase tracking-wider text-zinc-600">
            Implements
          </div>
          <div className="flex flex-wrap gap-1">
            {interfaces.map((iface) => (
              <button
                key={iface.name}
                onClick={() => iface.name && onSelectType(iface.name)}
                title={iface.name ?? undefined}
                className={ABSTRACT_CHIP_CLASS}
              >
                {iface.name ? middleEllipsis(iface.name, 24) : "?"}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Possible types (for interfaces/unions) */}
      {possibleTypes.length > 0 && (
        <div className="border-b border-border px-3 py-2">
          <div className="mb-1 text-[10px] font-medium uppercase tracking-wider text-zinc-600">
            Possible Types
          </div>
          <div className="flex flex-wrap gap-1">
            {possibleTypes.map((pt) => (
              <button
                key={pt.name}
                onClick={() => pt.name && onSelectType(pt.name)}
                title={pt.name ?? undefined}
                className="rounded bg-zinc-800 px-1.5 py-0.5 text-[10px] font-medium text-zinc-300 hover:bg-zinc-700"
              >
                {pt.name ? middleEllipsis(pt.name, 24) : "?"}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Fields */}
      {fields.length > 0 && (
        <div className="flex-1">
          <div className="sticky top-0 z-10 bg-surface-raised/95 backdrop-blur-sm px-3 py-1.5 text-[10px] font-medium uppercase tracking-wider text-zinc-600">
            Fields ({fields.length})
          </div>
          {fields.map((field) => (
            <FieldRow
              key={field.name}
              field={field}
              typeName={type.name}
              isSelected={selectedFieldName === field.name}
              onSelect={onSelectField}
            />
          ))}
        </div>
      )}

      {/* Enum values */}
      {enumValues.length > 0 && (
        <div className="flex-1">
          <div className="sticky top-0 z-10 bg-surface-raised/95 backdrop-blur-sm px-3 py-1.5 text-[10px] font-medium uppercase tracking-wider text-zinc-600">
            Values ({enumValues.length})
          </div>
          {enumValues.map((val) => (
            <EnumValueRow key={val.name} value={val} />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Field detail column (args + return type) ────────────────────────

function FieldDetailColumn({
  field,
  parentTypeName,
  selectedArgType,
  selectedReturnType,
  onSelectType,
  onSelectArg,
  onSelectReturnType,
  scrollRef,
}: {
  field: IntrospectionField;
  parentTypeName: string;
  selectedArgType?: string;
  selectedReturnType?: boolean;
  onSelectType?: (name: string) => void;
  onSelectArg?: (parentTypeName: string, fieldName: string, argTypeName: string) => void;
  onSelectReturnType?: (parentTypeName: string, fieldName: string) => void;
  scrollRef: React.RefObject<HTMLDivElement | null>;
}) {
  const returnTypeStr = formatTypeRef(field.type);
  const args = field.args ?? [];

  return (
    <div ref={scrollRef} className={`flex h-full ${COL_WIDTH} shrink-0 flex-col overflow-y-auto border-r border-border`}>
      {/* Field header */}
      <div className="sticky top-0 z-10 border-b border-border bg-surface-raised/95 backdrop-blur-sm px-3 py-2">
        <div className="flex items-center gap-2">
          <Tooltip text={field.name}>
            <span className={`text-sm font-semibold ${field.isDeprecated ? "line-through text-zinc-500" : "text-zinc-200"}`}>
              {middleEllipsis(field.name, 26)}
            </span>
          </Tooltip>
          <span className="shrink-0 rounded bg-zinc-700/50 px-1.5 py-0.5 text-[9px] font-medium text-zinc-400">
            FIELD
          </span>
        </div>
        <div className="mt-1 text-[11px] text-zinc-500">
          on <span className="text-zinc-400">{parentTypeName}</span>
        </div>
        {field.description && (
          <CollapsibleDescription text={field.description} />
        )}
        {field.isDeprecated && field.deprecationReason && (
          <div className={DEPRECATED_PANEL_CLASS}>
            <div className={DEPRECATED_LABEL_CLASS}>Deprecated</div>
            <SimpleMarkdown
              text={field.deprecationReason}
              className={DEPRECATED_TEXT_CLASS}
            />
          </div>
        )}
      </div>

      {/* Arguments */}
      {args.length > 0 && (
        <div className="border-b border-border">
          <div className="sticky top-0 z-10 bg-surface-raised/95 backdrop-blur-sm px-3 py-1.5 text-[10px] font-medium uppercase tracking-wider text-zinc-600">
            Arguments ({args.length})
          </div>
          {args.map((arg) => (
            <ArgRow
              key={arg.name}
              arg={arg}
              parentTypeName={parentTypeName}
              fieldName={field.name}
              isSelected={selectedArgType === arg.name}
              {...(onSelectArg !== undefined ? { onSelectArg } : {})}
              {...(onSelectType !== undefined ? { onSelectType } : {})}
            />
          ))}
        </div>
      )}

      {/* Return type */}
      <div>
        <div className="px-3 py-1.5 text-[10px] font-medium uppercase tracking-wider text-zinc-600">
          Returns
        </div>
        <button
          onClick={() => onSelectReturnType?.(parentTypeName, field.name)}
          className={`flex w-full items-center gap-2 px-3 py-2 text-left transition-colors ${
            selectedReturnType
              ? "bg-surface-overlay"
              : "hover:bg-surface-raised"
          }`}
        >
          <Tooltip text={returnTypeStr}>
            <span className={`text-xs font-medium ${TYPE_LINK_CLASS}`}>
              {middleEllipsis(returnTypeStr, 30)}
            </span>
          </Tooltip>
        </button>
      </div>
    </div>
  );
}

// ── Argument row ────────────────────────────────────────────────────

function ArgRow({
  arg,
  parentTypeName,
  fieldName,
  isSelected,
  onSelectArg,
  onSelectType,
}: {
  arg: IntrospectionArg;
  parentTypeName: string;
  fieldName: string;
  isSelected: boolean;
  onSelectArg?: (parentTypeName: string, fieldName: string, argTypeName: string) => void;
  onSelectType?: (name: string) => void;
}) {
  const typeRefStr = formatTypeRef(arg.type);
  const nameDisplay = middleEllipsis(arg.name, 22);
  const typeDisplay = middleEllipsis(typeRefStr, 18);

  const handleClick = () => {
    if (onSelectArg) {
      onSelectArg(parentTypeName, fieldName, arg.name);
    }
  };

  return (
    <button
      onClick={handleClick}
      className={`flex w-full flex-col gap-0.5 px-3 py-1.5 text-left transition-colors ${
        isSelected
          ? "bg-surface-overlay"
          : "hover:bg-surface-raised"
      }`}
    >
      <div className="flex items-center gap-1.5">
        <Tooltip text={arg.name}>
          <span className="text-xs font-medium text-schema-input">
            {nameDisplay}
          </span>
        </Tooltip>
        {arg.defaultValue != null && (
          <span className="shrink-0 text-[9px] text-zinc-600">
            = {arg.defaultValue}
          </span>
        )}
        <Tooltip text={typeRefStr}>
          <span className={`ml-auto shrink-0 text-[11px] ${TYPE_LINK_CLASS}`}>
            {typeDisplay}
          </span>
        </Tooltip>
      </div>
      {arg.description && (
        <FieldDescription text={arg.description} />
      )}
    </button>
  );
}

// ── Collapsible markdown description ────────────────────────────────

const DESC_COLLAPSE_THRESHOLD = 120; // chars

function CollapsibleDescription({ text }: { text: string }) {
  const [expanded, setExpanded] = useState(false);
  const isLong = text.length > DESC_COLLAPSE_THRESHOLD;

  const displayText = !isLong || expanded ? text : text.slice(0, DESC_COLLAPSE_THRESHOLD).trimEnd() + "…";

  return (
    <div className="mt-1 max-w-[280px]">
      <SimpleMarkdown
        text={displayText}
        className="text-[11px] leading-relaxed text-zinc-500"
      />
      {isLong && (
        <button
          onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
          className={`mt-0.5 text-[10px] ${TYPE_LINK_HOVER_CLASS}`}
        >
          {expanded ? "Show less" : "Show more"}
        </button>
      )}
    </div>
  );
}

// ── Field row ───────────────────────────────────────────────────────

function FieldRow({
  field,
  typeName,
  isSelected,
  onSelect,
}: {
  field: IntrospectionField;
  typeName: string;
  isSelected: boolean;
  onSelect: (typeName: string, fieldName: string) => void;
}) {
  const typeRefStr = formatTypeRef(field.type);
  const nameDisplay = middleEllipsis(field.name, 22);
  const typeDisplay = middleEllipsis(typeRefStr, 18);

  return (
    <button
      onClick={() => onSelect(typeName, field.name)}
      className={`flex w-full flex-col gap-0.5 px-3 py-1.5 text-left transition-colors ${
        isSelected
          ? "bg-surface-overlay"
          : "hover:bg-surface-raised"
      }`}
    >
      <div className="flex items-center gap-1.5">
        <Tooltip text={field.name}>
          <span className={`text-xs font-medium ${field.isDeprecated ? "line-through text-zinc-600" : "text-zinc-300"}`}>
            {nameDisplay}
          </span>
        </Tooltip>
        {field.args && field.args.length > 0 && (
          <span className="shrink-0 text-[10px] text-zinc-600">
            ({field.args.length} arg{field.args.length !== 1 ? "s" : ""})
          </span>
        )}
        {field.isDeprecated && (
          <span className={DEPRECATED_BADGE_CLASS}>
            deprecated
          </span>
        )}
        <Tooltip text={typeRefStr}>
          <span className={`ml-auto shrink-0 text-[11px] ${TYPE_LINK_CLASS}`}>
            {typeDisplay}
          </span>
        </Tooltip>
      </div>
      {field.isDeprecated && field.deprecationReason && (
        <SimpleMarkdown
          text={field.deprecationReason}
          className={DEPRECATED_TEXT_CLASS}
        />
      )}
      {field.description && (
        <FieldDescription text={field.description} />
      )}
    </button>
  );
}

const FIELD_DESC_THRESHOLD = 80;

function FieldDescription({ text }: { text: string }) {
  const [expanded, setExpanded] = useState(false);
  const isLong = text.length > FIELD_DESC_THRESHOLD;

  const displayText = !isLong || expanded ? text : text.slice(0, FIELD_DESC_THRESHOLD).trimEnd() + "…";

  return (
    <div className="max-w-[280px]">
      <SimpleMarkdown
        text={displayText}
        className="text-[10px] leading-relaxed text-zinc-600"
      />
      {isLong && (
        <button
          onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
          className={`text-[9px] ${TYPE_LINK_HOVER_CLASS}`}
        >
          {expanded ? "less" : "more"}
        </button>
      )}
    </div>
  );
}

// ── Enum value row ──────────────────────────────────────────────────

function EnumValueRow({ value }: { value: IntrospectionEnumValue }) {
  return (
    <div className="px-3 py-1.5">
      <div className="flex items-center gap-1.5">
        <Tooltip text={value.name}>
          <span className={`text-xs font-medium ${value.isDeprecated ? "line-through text-zinc-600" : "text-schema-enum"}`}>
            {middleEllipsis(value.name, 28)}
          </span>
        </Tooltip>
        {value.isDeprecated && (
          <span className={DEPRECATED_BADGE_CLASS}>
            deprecated
          </span>
        )}
      </div>
      {value.isDeprecated && value.deprecationReason && (
        <SimpleMarkdown
          text={value.deprecationReason}
          className={DEPRECATED_TEXT_CLASS}
        />
      )}
      {value.description && (
        <SimpleMarkdown
          text={value.description}
          className="max-w-[280px] text-[10px] leading-relaxed text-zinc-600"
        />
      )}
    </div>
  );
}
